import { createContext, useCallback, useContext, useRef } from "react";
import { useRouter } from "next/router";

type NavigationOptions =
  | {
      pathname?: string;
      newTab?: boolean;
      query?: Record<string, string | number>;
      search?: string;
      includeQueryParams?: boolean;
      skipHistory?: boolean;
      externalUrl?: false;
      internalUrl?: false;
    }
  | {
      url: string;
      externalUrl: true;
      internalUrl?: false;
    }
  | {
      url: string;
      internalUrl: true;
      externalUrl?: false;
    };

/**
 * Set by a surface that shows pages in place (e.g. a project drawer over another page): return true to
 * handle an in-app navigation to `pathname` yourself instead of leaving the current page.
 */
export const NavigationInterceptContext = createContext<((pathname: string) => boolean) | null>(null);

/**
 * Hook for setting default navigation settings
 * Wrapper for router.push
 */
export default function useNavigation() {
  const router = useRouter();
  const intercept = useContext(NavigationInterceptContext);
  const pendingTargetRef = useRef<string | null>(null);

  /**
   * Navigate using router push
   * Includes some default options such as shallow routing
   */
  const navigate = useCallback(
    (input: NavigationOptions) => {
      if (input.externalUrl) {
        return window.open(input.url, "_blank");
      }
      if (input.internalUrl) {
        if (intercept?.(new URL(input.url, window.location.origin).pathname)) {
          return;
        }
        return router.push(input.url);
      }

      const { pathname, query, search, includeQueryParams, newTab } = input;
      if (!newTab && pathname !== undefined && intercept?.(pathname)) {
        return;
      }
      const url = new URL(window.location.href);
      url.pathname = pathname ?? url.pathname;
      if (includeQueryParams) {
        Object.entries(query ?? {}).forEach(([key, value]) => url.searchParams.set(key, String(value)));
      } else {
        url.search = search ?? "";
      }

      if (newTab) {
        return window.open(url, "_blank");
      }

      const targetPathname = pathname ?? router.pathname;
      const targetUrl = includeQueryParams ? url.pathname + url.search : url.pathname;
      // router.asPath lags a route change in flight; window.location is the committed URL
      const currentUrl = includeQueryParams
        ? window.location.pathname + window.location.search
        : window.location.pathname;

      if (targetUrl === currentUrl) {
        return;
      }

      // An effect that re-fires before the push commits would otherwise navigate to the URL the
      // router is already landing on, which Next.js rejects with a hard-navigation invariant
      const resolvedTarget = url.pathname + url.search;
      if (pendingTargetRef.current === resolvedTarget) {
        return;
      }
      pendingTargetRef.current = resolvedTarget;

      const fn = input.skipHistory ? router.replace : router.push;

      return fn(
        {
          pathname: targetPathname,
          search: url.search,
          query: includeQueryParams
            ? {
                ...router.query,
                ...query,
              }
            : null,
        },
        undefined,
        { shallow: true },
      ).finally(() => {
        pendingTargetRef.current = null;
      });
    },
    [router, intercept],
  );

  const navigateHref = useCallback((href: string, options?: { stripParams?: boolean }) => {
    return {
      pathname: href,
      search: options?.stripParams ? "" : window.location.search,
    };
  }, []);

  return { navigate, navigateHref };
}
